import { NumericFormat } from 'react-number-format';

import { Button, IconButton } from '@mui/material';

import { NQtyIcon, PQtyIcon, StarIcon } from '../../../assets/svg';
import { RewardRuleEffects } from '../../../types';
import { usePayLinkCart } from '../context/PayLinkCartContext';
import { RewardTypeIcon } from './RewardTypeIcon';

const getRewardLabel = (rewardEffect?: string, rewardEffectValue?: string) => {
  const rewardValue = Number(rewardEffectValue ?? 0);

  if (rewardEffect === RewardRuleEffects.AddDiscount) {
    return `${rewardValue}% off`;
  }

  if (
    rewardEffect === RewardRuleEffects.AddPoints ||
    rewardEffect === RewardRuleEffects.Points
  ) {
    return `${rewardValue} points`;
  }

  if (rewardEffect === RewardRuleEffects.AddFreeItem) {
    return rewardValue > 0 ? `Get ${rewardValue} free item(s)` : 'Get free item';
  }

  if (rewardEffect === RewardRuleEffects.CashBack) {
    return rewardValue > 0 ? `${rewardValue} cashback` : 'Cashback reward';
  }

  if (
    rewardEffect === RewardRuleEffects.FreeShipping ||
    rewardEffect === RewardRuleEffects.Shipping
  ) {
    return 'Free shipping';
  }

  return '';
};

const getRewardIconType = (rewardEffect?: string) => {
  if (rewardEffect === RewardRuleEffects.FreeShipping) {
    return RewardRuleEffects.Shipping;
  }

  return rewardEffect as RewardRuleEffects | undefined;
};

export const CartItem = () => {
  const { cartItems, incrementItem, decrementItem, removeFromCart, currency } =
    usePayLinkCart();

  if (!cartItems.length) {
    return (
      <div className="border border-dashed border-py_black-200 rounded-2xl p-6 text-sm text-py_black-700">
        Your cart is empty. Add products from the store to continue.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {cartItems.map((item) => {
          const rewardEffect = item.loyaltyPromotion?.rewardEffect;
          const rewardEffectValue = item.loyaltyPromotion?.rewardEffectValue;
          const rewardLabel = getRewardLabel(rewardEffect, rewardEffectValue);
          const rewardIconType = getRewardIconType(rewardEffect);
          const hasDiscountReward =
            rewardEffect === RewardRuleEffects.AddDiscount &&
            Number(rewardEffectValue) > 0;

        return (
        <div
          key={item.productCatalogueItemUniqueReferenceNumber}
          className="border border-py_black-100 rounded-2xl p-2 flex flex-col gap-3"
        >
          <div className="inline-flex gap-2 w-full lg:w-9/12">
            <div className="border border-py_black-100 rounded-lg min-w-[110px] h-[110px] overflow-hidden">
              <img
                src={item.productImages?.[0]?.documentAbsoluteUri}
                alt={item.name}
                width={110}
                height={110}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="inline-flex flex-col gap-2">
                <h5 className="text-[0.875rem] text-py_black-950 font-medium leading-[140%]">
                  {item.name}
                </h5>

                <div className="inline-flex items-center gap-1 text-xs text-py_black-600 font-normal leading-[140%]">
                  4.5 <StarIcon bg="#FFCF0F" />
                </div>
              </div>

              <div className="inline-flex items-center justify-between mt-auto">
                <div className="inline-flex items-center gap-1">
                  <div className="text-[1rem] text-py_black-600 font-semibold">
                    <NumericFormat
                      value={item.price}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={currency}
                      className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
                    />
                  </div>

                  {hasDiscountReward && (
                    <del className="text-[0.625rem] text-py_black-600 font-normal leading-[140%] tracking-[-0.02em]">
                      <NumericFormat
                        value={
                          Number(item.price) +
                          Number(rewardEffectValue)
                        }
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={currency}
                      />
                    </del>
                  )}
                </div>

                {rewardIconType && rewardLabel && (
                  <RewardTypeIcon
                    rewardType={rewardIconType}
                    text={rewardLabel}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-2">
            <div className="inline-flex items-center gap-2">
              <span className="text-xs text-py_blue-700 font-semibold leading-[140%]">
                Qty:
              </span>
              <div className="inline-flex items-center gap-2 px-2 py-1 border border-py_black-100 rounded-[0.25rem]">
                <IconButton
                  className="!p-0"
                  onClick={() =>
                    decrementItem(
                      item.productCatalogueItemUniqueReferenceNumber,
                    )
                  }
                >
                  <NQtyIcon />
                </IconButton>
                <div className="qty">{item.quantity}</div>
                <IconButton
                  className="!p-0"
                  onClick={() =>
                    incrementItem(
                      item.productCatalogueItemUniqueReferenceNumber,
                    )
                  }
                >
                  <PQtyIcon />
                </IconButton>
              </div>
            </div>

            <Button
              className="!text-xs !text-py_black-400 !capitalize !font-medium !leading-[140%]"
              onClick={() =>
                removeFromCart(item.productCatalogueItemUniqueReferenceNumber)
              }
            >
              Remove
            </Button>
          </div>
        </div>
        );
      })}
    </div>
  );
};
